import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';

import { Button, Tooltip } from '@getro/rombo';

const BackupCodes = ({ codes, onDone, fullWidth }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const codesText = codes.join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([`${codesText}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'getro-backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Text fontSize="14px" lineHeight="1.4" mb={3}>
        Save these backup codes somewhere safe. Each one can be used once if you lose access to your authenticator app.
        They won&apos;t be shown again.
      </Text>
      <Box
        as="ul"
        p={3}
        my={2}
        sx={{
          listStyle: 'none',
          bg: 'neutral.20',
          borderRadius: '8px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          justifyItems: 'center',
          gap: 2,
          fontFamily: 'monospace',
        }}
      >
        {codes.map((code) => (
          <Box as="li" key={code} data-cy="mfa-backup-code">
            {code}
          </Box>
        ))}
      </Box>
      <Flex mb={3} sx={{ gap: 2 }}>
        <Tooltip
          position="top"
          openWith={[]}
          isOpen={copied}
          content={<Text fontSize="12px">Copied!</Text>}
          trigger={
            <Button
              variant="secondary"
              size="small"
              onClick={handleCopy}
              {...(fullWidth ? { width: '100%', justifyContent: 'center' } : {})}
            >
              Copy
            </Button>
          }
        />
        <Button
          variant="secondary"
          size="small"
          onClick={handleDownload}
          {...(fullWidth ? { width: '100%', justifyContent: 'center' } : {})}
        >
          Download
        </Button>
      </Flex>
      <Flex as="label" alignItems="center" sx={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          data-cy="mfa-backup-confirm"
        />
        <Text as="span" ml={2} fontSize="14px">
          I&apos;ve saved my backup codes
        </Text>
      </Flex>
      <Box mt={3}>
        <Button
          disabled={!confirmed}
          onClick={onDone}
          data-cy="mfa-backup-done"
          {...(fullWidth ? { width: '100%', justifyContent: 'center', size: 'large' } : {})}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
};

BackupCodes.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onDone: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
};

BackupCodes.defaultProps = {
  fullWidth: false,
};

export default BackupCodes;
